"use client";

import {
  Avatar,
  AvatarImage,
  InitialsAvatarFallback,
} from "@workspace/ui/components/avatar";
import { Badge } from "@workspace/ui/components/badge";
import { cn } from "@workspace/ui/lib/utils";
import { formatDistance } from "date-fns";
import { Icons } from "@/components/icons";
import { COMMITMENTS, PRIORITIES, WORK_MODELS } from "@/config/constants";
import { getBadgeColorVariant } from "@/lib/badge-color-variant";
import {
  formatCompensation,
  isMarketRateCompensation,
} from "@/lib/compensation";
import {
  formatListingPlaceName,
  formatNearbyDistanceKm,
  getOptionLabel,
  haversineDistanceKm,
} from "./marketplace-helpers";
import type { MarketplaceJob } from "./use-cluster";

type ListingRowProps = {
  job: MarketplaceJob;
  /** Phase 5 wiring: highlight the row matching the selected/hovered marker. */
  isActive?: boolean;
  /** Phase 5 wiring: open the job detail popover/drawer. */
  onSelect?: (job: MarketplaceJob) => void;
  /** Phase 5 wiring: marker highlight on hover. */
  onHover?: (id: string | null) => void;
  /** DOM id prefix so the cluster popover can avoid clashing with the list's ids. */
  idPrefix?: string;
  /** User geolocation, used to show the distance from the job. */
  userLatitude?: number;
  userLongitude?: number;
};

export function ListingRow({
  job,
  isActive = false,
  onSelect,
  onHover,
  idPrefix = "listing",
  userLatitude,
  userLongitude,
}: ListingRowProps) {
  const workModelLabel = getOptionLabel(WORK_MODELS, job.work_model);
  const commitmentLabel = getOptionLabel(COMMITMENTS, job.commitment);
  const priorityLabel = getOptionLabel(PRIORITIES, job.priority);
  const placeName = formatListingPlaceName(job.storeName ?? job.postedBy ?? "");
  const isMarketRate = isMarketRateCompensation(job.compensation);
  const compensationLabel = formatCompensation(job.compensation);

  // GeoJSON coordinates are [longitude, latitude].
  const [jobLongitude, jobLatitude] = job.address.location.coordinates as [
    number,
    number,
  ];
  const distanceLabel =
    userLatitude !== undefined && userLongitude !== undefined
      ? formatNearbyDistanceKm(
          haversineDistanceKm(
            userLatitude,
            userLongitude,
            jobLatitude,
            jobLongitude
          )
        )
      : null;

  const handleMouseEnter = () => onHover?.(job.id);
  const handleMouseLeave = () => onHover?.(null);
  const handleFocus = () => onHover?.(job.id);
  const handleBlur = () => onHover?.(null);

  return (
    <button
      className={cn(
        "group/listing-row grid w-full grid-cols-[2.25rem_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 px-3 py-2.5 text-left transition-[background-color,scale] hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-inset active:scale-[0.99]",
        isActive && "bg-muted/70 ring-1 ring-ring/40 ring-inset"
      )}
      data-active={isActive || undefined}
      id={`${idPrefix}-${job.id}`}
      onBlur={handleBlur}
      onClick={() => onSelect?.(job)}
      onFocus={handleFocus}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      type="button"
    >
      <Avatar className="mt-0.5 size-9 shrink-0 rounded-full ring-1 ring-border/80">
        <AvatarImage
          alt={job.postedBy ?? undefined}
          src={job.postedByImage ?? undefined}
        />
        <InitialsAvatarFallback name={job.postedBy ?? ""} />
      </Avatar>

      <div className="grid min-w-0 gap-1">
        <div className="min-w-0">
          <p className="truncate font-semibold text-sm leading-5">
            {job.title}
          </p>
          <p className="flex min-w-0 items-center gap-1.5 text-muted-foreground text-xs leading-4">
            <span className="truncate">{placeName}</span>
            {distanceLabel ? (
              <>
                <span aria-hidden className="text-border">
                  ·
                </span>
                <span className="inline-flex shrink-0 items-center gap-0.5 tabular-nums">
                  <Icons.locate className="size-3" />
                  {distanceLabel}
                </span>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          <Badge
            className="max-w-36 shrink-0 whitespace-nowrap"
            variant={getBadgeColorVariant(job.work_model)}
          >
            <span className="truncate">{workModelLabel}</span>
          </Badge>
          <Badge
            className="max-w-36 shrink-0 whitespace-nowrap"
            variant={getBadgeColorVariant(job.commitment)}
          >
            <span className="truncate">{commitmentLabel}</span>
          </Badge>
          {job.priority === "high" && (
            <Badge
              className="max-w-36 shrink-0 whitespace-nowrap"
              variant="amber"
            >
              <span className="truncate">{priorityLabel}</span>
            </Badge>
          )}
        </div>
      </div>

      <div className="flex w-[6.75rem] shrink-0 flex-col items-end gap-1 text-right">
        <span
          className={cn(
            "max-w-full truncate text-xs tabular-nums leading-4",
            isMarketRate
              ? "text-muted-foreground"
              : "rounded-sm bg-muted px-1.5 py-0.5 font-semibold"
          )}
          title={compensationLabel}
        >
          {compensationLabel}
        </span>
        <span
          className="text-[11px] text-muted-foreground leading-4"
          suppressHydrationWarning
        >
          {/* react-doctor-disable-next-line react-doctor/rendering-hydration-mismatch-time -- relative time is client-only; the list never server-renders with live data (also has suppressHydrationWarning) */}
          {formatDistance(job.createdAt, new Date(), {
            addSuffix: true,
          })}
        </span>
      </div>
    </button>
  );
}

export function ListingRowSkeleton() {
  return (
    <div className="grid w-full grid-cols-[2.25rem_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 px-3 py-2.5">
      <div className="mt-0.5 size-9 shrink-0 animate-pulse rounded-full bg-muted ring-1 ring-border/80" />
      <div className="grid min-w-0 gap-1">
        <div className="grid min-w-0 gap-1">
          <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
        </div>
        <div className="flex items-center gap-1">
          <div className="h-5 w-14 animate-pulse rounded bg-muted" />
          <div className="h-5 w-16 animate-pulse rounded bg-muted" />
        </div>
      </div>
      <div className="flex w-[6.75rem] flex-col items-end gap-1">
        <div className="h-4 w-16 animate-pulse rounded bg-muted" />
        <div className="h-3 w-14 animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}
